import dayjs from 'dayjs';
import { makeAutoObservable } from 'mobx';
import { isCoexistableCard } from '../../components/TimetableVersion/graphql/types';
import { isDay } from '../../utils/typeguards';
import { TimetableCardUuid } from '../GridCardsStore';
import type { PinboardStore } from './PinboardStore';
import { CardAvailability, Time } from './TimeAvailabilityStore';

export class ConflictStore {
  public cardConflicts: Map<TimetableCardUuid, CardAvailability> = new Map();

  constructor(private root: PinboardStore) {
    makeAutoObservable(this);
  }

  /** Koexistierbarkeit einer belegten Karte (elective/attendance) für die Konflikt-Unterdrückung. */
  private _isCardCoexistable = (cardUuid: string): boolean => {
    const card = this.root.getCardRaw(cardUuid);
    return card ? isCoexistableCard(card) : false;
  };

  public _getAffectedCardsForCard(cardUuid: string): Set<string> {
    const affected = new Set<string>();
    const day = this.root.placedCardsDay.get(cardUuid);
    const cells = this.root.placedCardsTimeGridEntries.get(cardUuid);
    if (!day || !cells) return affected;

    this.root.cardTeacher.get(cardUuid)?.forEach((teacherUuid) => {
      cells.forEach((cell) => {
        this.root.cardsBySlotAndResource.get(`${cell}:${day}:${teacherUuid}`)?.forEach((c) => affected.add(c));
      });
    });
    this.root.cardClasses.get(cardUuid)?.forEach((classUuid) => {
      cells.forEach((cell) => {
        this.root.cardsBySlotAndResource.get(`${cell}:${day}:${classUuid}`)?.forEach((c) => affected.add(c));
      });
    });
    this.root.cardRooms.get(cardUuid)?.forEach((room) => {
      cells.forEach((cell) => {
        this.root.cardsBySlotAndResource.get(`${cell}:${day}:${room.value}`)?.forEach((c) => affected.add(c));
      });
    });
    return affected;
  }

  public updateConflictsForCards(cardUuids: Set<string>) {
    cardUuids.forEach((cardUuid) => this.cardConflicts.delete(cardUuid));

    cardUuids.forEach((cardUuid) => {
      const day = this.root.placedCardsDay.get(cardUuid);
      const cells = this.root.placedCardsTimeGridEntries.get(cardUuid);
      if (!day || !cells || !isDay(day)) return;

      const times: Time[] = [];
      cells.forEach((entry) => {
        const timeGridEntry = this.root.timeGridEntries?.find((e) => e.uuid === entry);
        const time = timeGridEntry ? (dayjs(timeGridEntry.start).add(1, 'minute').format('HH:mm') as Time) : false;
        if (time) times.push(time);
      });

      const _card = this.root.getAvailableCardForCardUuid(cardUuid);
      if (_card && isDay(day)) {
        const availability = this.root.getTimeAvailabilityStore()?.checkResourceConflicts({
          ..._card,
          day,
          times,
          subjectContainerSlots: this.root.subjectContainerSlots.get(cardUuid),
          getSlotsForCard: (uuid) => this.root.subjectContainerSlots.get(uuid),
          isCoexistable: this._isCardCoexistable(cardUuid),
          isCardCoexistable: this._isCardCoexistable,
        });
        if (availability && availability.size > 0) {
          this.cardConflicts.set(cardUuid, availability);
        }
      }
    });
  }

  public setConflicts() {
    this.removeConflicts();
    this.root.placedCardsDay.forEach((weekday, cardUuid) => {
      const timeGridEntries = this.root.placedCardsTimeGridEntries.get(cardUuid);
      const times: Time[] = [];

      if (timeGridEntries) {
        timeGridEntries.forEach((entry) => {
          const timeGridEntry = this.root.timeGridEntries?.find((e) => e.uuid === entry);
          const time = timeGridEntry ? (dayjs(timeGridEntry?.start).add(1, 'minute').format('HH:mm') as Time) : false;
          if (time) times.push(time);
        });

        const _card = this.root.getAvailableCardForCardUuid(cardUuid);
        if (_card && isDay(weekday)) {
          const availability = this.root.getTimeAvailabilityStore()?.checkResourceConflicts({
            ..._card,
            day: weekday,
            times,
            subjectContainerSlots: this.root.subjectContainerSlots.get(cardUuid),
            getSlotsForCard: (uuid) => this.root.subjectContainerSlots.get(uuid),
            isCoexistable: this._isCardCoexistable(cardUuid),
            isCardCoexistable: this._isCardCoexistable,
          });
          if (availability && availability.size > 0) {
            this.cardConflicts.set(cardUuid, availability);
          }
        }
      }
    });
  }

  public removeConflicts() {
    this.cardConflicts = new Map();
  }
}
